/**
 * BackgroundSessionValidator
 *
 * Encapsulates the retry-aware background session validation logic previously
 * inlined in SecureAuthProvider. Handles:
 *  - Consecutive failure tracking with configurable threshold
 *  - One automatic retry after threshold is reached
 *  - Transient error cooldown (network failures don't count as validation failures)
 *  - Stale request detection (newer validate() calls supersede older ones)
 *  - Clean destruction with timeout cancellation
 */
import { authLogger } from '@/core/utils/logger';

const DEFAULT_MAX_CONSECUTIVE_FAILURES = 2;
const DEFAULT_TRANSIENT_COOLDOWN_MS = 60_000;
const DEFAULT_RETRY_DELAY_MS = 2_000;

export interface BackgroundSessionValidatorOptions {
  /** Async function that returns true if session is valid */
  validateSession: () => Promise<boolean>;
  /** Called when session is confirmed invalid after retries */
  onSessionInvalid: () => void;
  maxConsecutiveFailures?: number;
  transientCooldownMs?: number;
  retryDelayMs?: number;
}

export class BackgroundSessionValidator {
  private consecutiveFailures = 0;
  private lastTransientFailure = 0;
  private currentRequestId = 0;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  private readonly validateSession: () => Promise<boolean>;
  private readonly onSessionInvalid: () => void;
  private readonly maxConsecutiveFailures: number;
  private readonly transientCooldownMs: number;
  private readonly retryDelayMs: number;

  constructor(options: BackgroundSessionValidatorOptions) {
    this.validateSession = options.validateSession;
    this.onSessionInvalid = options.onSessionInvalid;
    this.maxConsecutiveFailures =
      options.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_FAILURES;
    this.transientCooldownMs =
      options.transientCooldownMs ?? DEFAULT_TRANSIENT_COOLDOWN_MS;
    this.retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;
  }

  /** Whether validation should be skipped due to recent transient error */
  isInCooldown(): boolean {
    if (this.lastTransientFailure === 0) return false;
    return Date.now() - this.lastTransientFailure < this.transientCooldownMs;
  }

  /**
   * Run background validation. Non-blocking — fires and forgets internally.
   * If called while a previous validation is in-flight, the older result
   * is discarded (stale request detection).
   */
  async validate(): Promise<void> {
    if (this.destroyed) return;

    if (this.isInCooldown()) {
      const elapsed = Date.now() - this.lastTransientFailure;
      authLogger.info(
        `[BackgroundSessionValidator] Skipping - transient cooldown (${Math.round(elapsed / 1000)}s/${this.transientCooldownMs / 1000}s)`
      );
      return;
    }

    const requestId = ++this.currentRequestId;
    await this.performValidation(requestId, false);
  }

  /** Reset all internal state (e.g., on fresh login) */
  reset(): void {
    this.consecutiveFailures = 0;
    this.lastTransientFailure = 0;
    this.currentRequestId = 0;
  }

  /** Cancel pending retries and mark as destroyed */
  destroy(): void {
    this.destroyed = true;
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  // ── private ──────────────────────────────────────────────────────────

  private async performValidation(
    requestId: number,
    isRetry: boolean
  ): Promise<void> {
    if (this.destroyed) return;

    try {
      const isValid = await this.validateSession();

      // Stale check: a newer validate() call supersedes this one
      if (this.destroyed || requestId !== this.currentRequestId) {
        authLogger.debug(
          '[BackgroundSessionValidator] Ignoring stale validation result'
        );
        return;
      }

      if (isValid) {
        this.consecutiveFailures = 0;
        this.lastTransientFailure = 0;
        return;
      }

      // Validation returned false — increment failure counter
      this.consecutiveFailures++;
      authLogger.debug(
        `[BackgroundSessionValidator] Validation failed (${this.consecutiveFailures}/${this.maxConsecutiveFailures})`
      );

      if (this.consecutiveFailures < this.maxConsecutiveFailures) {
        return;
      }

      // Threshold reached
      if (!isRetry) {
        authLogger.debug(
          '[BackgroundSessionValidator] Threshold reached, retrying after delay...'
        );
        await this.delay(this.retryDelayMs);

        if (this.destroyed || requestId !== this.currentRequestId) {
          authLogger.debug(
            '[BackgroundSessionValidator] Aborted during retry delay'
          );
          return;
        }
        return this.performValidation(requestId, true);
      }

      // Retry also failed — session is truly invalid
      if (this.destroyed) return;
      authLogger.info(
        '[BackgroundSessionValidator] Validation failed after retry, session invalid'
      );
      this.consecutiveFailures = 0;
      this.onSessionInvalid();
    } catch (e) {
      // Network/transient errors don't count as validation failures
      authLogger.debug(
        '[BackgroundSessionValidator] Validation error (transient):',
        e
      );
      this.lastTransientFailure = Date.now();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = null;
        resolve();
      }, ms);
    });
  }
}
